/* A Leading Role - ALR skin for the Smile.io rewards launcher and panel.
 *
 * Smile renders its launcher and panel into same-origin frames (srcdoc), so
 * the theme can style them directly. Additive on purpose: Smile's own script
 * is left untouched; this watches for its frames and drops a stylesheet in.
 * Everything is CSS plus one label change - nothing here touches Smile's
 * behaviour or data, and it stands down quietly if Smile ever changes shape.
 *
 * Smile ships two launchers: a "lite" one on first paint (a bare <button> with
 * a <span>) and the full one once its app loads (.launcher-button wrapping a
 * .launcher-container pill with .launcher-text). Both are covered below.
 *
 * Palette: maroon #5B1220 / deep maroon #3E0C16 / gold #C9A227 / cream #FBF7F0
 * / stage cream #F6EBD2. Gold is decorative or on maroon only - it fails
 * contrast as text on cream, so readable text is maroon or muted brown.
 */
(function () {
  var LAUNCHER_LABEL = 'Front Row Rewards';
  var SERIF = "Georgia,'Times New Roman',serif";
  var SHADOW = '0 6px 18px rgba(24,0,2,.28)';

  var PANEL_CSS = [
    "@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600&family=Jost:wght@300;400;500&display=swap');",
    '[style*="--banner-base-colour"]{--banner-base-colour:#5B1220!important;--banner-gradient-dark-colour:#3E0C16!important;',
    '--button-base-colour:#5B1220!important;--button-hover-colour:#6E1A2B!important;--button-active-colour:#3E0C16!important;--link-colour:#8A5A1E!important}',
    "body,button,input,a,p,span,div,li{font-family:'Jost','Helvetica Neue',Arial,sans-serif}",
    ".heading,.display,h1,h2,h3,.header-primary,.header-secondary{font-family:'Playfair Display',Georgia,serif!important;letter-spacing:.02em}",
    '.panel-wrapper,.frame-content{background:#F6EBD2!important}',
    '.background-gradient{background:linear-gradient(160deg,#5B1220 0%,#3E0C16 100%)!important}',
    '.panel-card-container,.join-program-card{background:#FBF7F0!important;border:1px solid rgba(201,162,39,.35)!important}',
    '.btn-primary,a.btn-primary,button.btn-primary{background:#5B1220!important;border-color:#5B1220!important;color:#FBF7F0!important;',
    'font-family:' + SERIF + '!important;letter-spacing:.08em!important;text-transform:uppercase!important;font-size:12px!important}',
    '.btn-primary:hover{background:#6E1A2B!important;border-color:#6E1A2B!important}',
    'a:not(.btn-primary){color:#8A5A1E!important}',
    '.card-list-item{color:#2A1A1C!important}',
    '.card-list-item:hover{background:rgba(201,162,39,.10)!important}'
  ].join('\n');

  var LAUNCHER_CSS = [
    // Shared type for both launcher variants.
    'button{font-family:' + SERIF + '!important;font-size:12px!important;letter-spacing:.14em!important;',
    'text-transform:uppercase!important;color:#FBF7F0!important}',
    // Lite launcher: the button is the pill.
    'button:not(.launcher-button){background:#5B1220!important;border:1px solid #C9A227!important;padding:15px 22px!important;box-shadow:' + SHADOW + '!important}',
    'button:not(.launcher-button):hover{background:#6E1A2B!important}',
    // Full launcher: the button is a transparent wrapper, the pill is inside.
    '.launcher-button{background:transparent!important;border:0!important;padding:0!important;box-shadow:none!important;display:inline-block!important;width:auto!important}',
    '.launcher-container{display:inline-flex!important;width:auto!important;justify-content:flex-start!important;background:#5B1220!important;',
    'border:1px solid #C9A227!important;color:#FBF7F0!important;border-radius:30px!important;padding:15px 22px!important;box-shadow:' + SHADOW + '!important}',
    '.launcher-container:hover{background:#6E1A2B!important}',
    '.launcher-content-container{width:auto!important}',
    '.launcher-text{font-family:' + SERIF + '!important;font-size:12px!important;letter-spacing:.14em!important;text-transform:uppercase!important;white-space:nowrap!important;color:#FBF7F0!important}',
    '.launcher-icon,button img{display:none!important}',
    "button span::before,.launcher-text::before{content:'\\2726  ';color:#C9A227}",
    // Narrow frame (phones - the host shrinks the frame to 60px): a gold star
    // in a maroon circle, so nothing crowds the Concierge button.
    '@media (max-width:120px){',
    'button span,.launcher-text{display:none!important}',
    'button:not(.launcher-button),.launcher-container{width:58px!important;height:58px!important;padding:0!important;border-radius:50%!important;',
    'display:inline-flex!important;align-items:center!important;justify-content:center!important}',
    "button:not(.launcher-button)::before,.launcher-content-container::before{content:'\\2726';color:#C9A227;font-size:20px;line-height:1}",
    '}'
  ].join('\n');

  // Host page: keep the launcher (and, on wide screens, the panel) bottom-left,
  // clear of the Concierge button. Smile sizes the full launcher's frame to its
  // own default label, so give it room for ours.
  var HOST_CSS = [
    '#smile-ui-lite-launcher-frame-container,.smile-launcher-frame-container{left:20px!important;right:auto!important}',
    '#smile-ui-lite-launcher-frame-container,.smile-launcher-frame-container{width:260px!important;box-shadow:none!important}',
    '#smile-lite-launcher-frame,.smile-launcher-frame{width:100%!important}',
    '.smile-panel-frame-container{background-color:#F6EBD2!important}',
    '@media (min-width:768px){.smile-panel-frame-container{left:20px!important;right:auto!important}}',
    '@media (max-width:767px){#smile-ui-lite-launcher-frame-container,.smile-launcher-frame-container{width:60px!important}}'
  ].join('\n');

  function doc(frame) {
    try { return frame.contentDocument; } catch (err) { return null; }
  }

  function inject(d, css, id) {
    if (!d || !d.head || d.getElementById(id)) return false;
    var s = d.createElement('style');
    s.id = id;
    s.textContent = css;
    d.head.appendChild(s);
    return true;
  }

  function relabel(d) {
    var t = d.querySelector('.launcher-text') || d.querySelector('button span');
    if (t && t.textContent !== LAUNCHER_LABEL) t.textContent = LAUNCHER_LABEL;
  }

  function skinLauncher(frame) {
    var d = doc(frame);
    if (!d) return;
    inject(d, LAUNCHER_CSS, 'alr-smile-launcher');
    relabel(d);
    // Smile re-renders the label inside the frame; keep ours.
    if (!d.__alrWatched && d.body) {
      d.__alrWatched = true;
      new MutationObserver(function () { relabel(d); })
        .observe(d.body, { childList: true, subtree: true, characterData: true });
    }
  }

  function skinPanel(frame) {
    var d = doc(frame);
    if (!d) return;
    inject(d, PANEL_CSS, 'alr-smile-panel');
  }

  function sweep() {
    var frames = document.querySelectorAll('iframe');
    for (var i = 0; i < frames.length; i++) {
      var t = frames[i].getAttribute('title') || '';
      if (/open loyalty program/i.test(t)) skinLauncher(frames[i]);
      else if (/loyalty program pop-up/i.test(t)) skinPanel(frames[i]);
    }
  }

  function start() {
    inject(document, HOST_CSS, 'alr-smile-host');
    sweep();
    // Smile builds its frames after load, swaps the lite launcher for the full
    // one, and rebuilds the panel on open, so keep watching. A frame's inner
    // document can also be replaced, hence the periodic re-sweep for the
    // first minute.
    var mo = new MutationObserver(sweep);
    mo.observe(document.documentElement, { childList: true, subtree: true });
    var n = 0;
    var t = setInterval(function () {
      sweep();
      if (++n > 60) clearInterval(t);
    }, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
